import { db, FirebaseTimestamp } from "../firebase";
import { arrayUnion, doc, updateDoc, writeBatch } from "firebase/firestore";
import {
  hideLoadingAction,
  showLoadingAction,
} from "../reducks/loading/actions";
import {
  buildChangeLogEntry,
  buildDateEditUpdate,
  buildStatusChangeUpdate,
  CHANGE_LOG_FIELD,
  CHANGE_LOG_TYPE_DATE,
  CHANGE_LOG_TYPE_STATUS,
} from "./order/orderDateUpdate";

// 日付の書き込みは必ずTimestampで行う。
// export.js / utils/formatting.js は timestamp.seconds を直読みしており、
// Dateや文字列が混じると "NaN-NaN-NaN" になるため。
const toFirestoreValue = (value) =>
  value instanceof Date ? FirebaseTimestamp.fromDate(value) : value;

const toFirestoreFields = (fields) =>
  Object.entries(fields).reduce(
    (acc, [key, value]) => ({ ...acc, [key]: toFirestoreValue(value) }),
    {}
  );

const toFirestoreChangeLog = (entry) => ({
  at: toFirestoreValue(entry.at),
  by: entry.by,
  type: entry.type,
  changes: entry.changes.map(({ field, from, to }) => ({
    field,
    from: toFirestoreValue(from),
    to: toFirestoreValue(to),
  })),
});

// 更新データ(ステータス・日付・監査項目・変更履歴)を1つにまとめる
const composeUpdateData = ({ fields, changes, type, userInfo, now }) => {
  const updateData = {
    ...toFirestoreFields(fields),
    updated_at: toFirestoreValue(now),
    update_user_email: userInfo?.email ?? "",
  };
  const entry = buildChangeLogEntry({
    type,
    changes,
    userEmail: userInfo?.email,
    now,
  });
  if (entry) {
    // 新規作成時にはこのフィールドを作らない(arrayUnionで初回追記時に生える)。
    // そのため buildOrderData とそのテストには影響しない。
    updateData[CHANGE_LOG_FIELD] = arrayUnion(toFirestoreChangeLog(entry));
  }
  return updateData;
};

const toTargetList = (orders) =>
  (Array.isArray(orders) ? orders : [orders]).filter(Boolean);

// Firestoreのバッチ書き込みは1コミット500件までなので、分割してコミットする。
//   buildUpdate(order) が null を返した注文は書き込み対象から外す。
// 戻り値: 実際に書き込んだ件数
const BATCH_SIZE = 500;
const commitInChunks = async (targets, buildUpdate) => {
  const writes = [];
  targets.forEach((order) => {
    const updateData = buildUpdate(order);
    if (updateData) {
      writes.push({ ref: doc(db, "orders", order.id), updateData });
    }
  });
  for (let i = 0; i < writes.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    writes.slice(i, i + BATCH_SIZE).forEach(({ ref, updateData }) => {
      batch.update(ref, updateData);
    });
    await batch.commit();
  }
  return writes.length;
};

// 単票のステータス更新。
//   order     … 注文doc(id と現在のステータス・日付が必要)
//   dateInput … { enabled, date } 省略時は「既存の日付を維持(未設定なら現在時刻)」
export const updateOrderStatusToFireStore = async (
  status,
  order,
  userInfo,
  dateInput
) => {
  try {
    const now = new Date();
    const { fields, changes } = buildStatusChangeUpdate({
      order,
      targetStatus: status,
      dateInput,
      now,
    });
    const orderRef = doc(db, "orders", order.id);
    await updateDoc(
      orderRef,
      composeUpdateData({
        fields,
        changes,
        type: CHANGE_LOG_TYPE_STATUS,
        userInfo,
        now,
      })
    );
  } catch (error) {
    // 呼び出し側でハンドリングできるよう必ず再throwする
    // (一括版と挙動を揃える。握りつぶすと失敗が画面に出ない)
    console.error("Error updating order status: ", error);
    throw error;
  }
};

// 一括のステータス更新。
//   orders    … 注文docの配列(idだけでなく現在のステータス・日付が必要)
//   dateInput … { enabled, date } 省略時は「既存の日付を維持(未設定なら現在時刻)」
export const updateMultiOrdersStatusToFireStore = (
  status,
  orders,
  userInfo,
  dateInput
) => {
  return async (dispatch) => {
    try {
      dispatch(showLoadingAction("更新中..."));
      const now = new Date();
      const targets = toTargetList(orders);
      await commitInChunks(targets, (order) => {
        const { fields, changes } = buildStatusChangeUpdate({
          order,
          targetStatus: status,
          dateInput,
          now,
        });
        return composeUpdateData({
          fields,
          changes,
          type: CHANGE_LOG_TYPE_STATUS,
          userInfo,
          now,
        });
      });
    } catch (error) {
      // 呼び出し側でハンドリングできるよう必ず再throwする
      // （握りつぶすと「更新失敗なのにメール送信フォームが開く」等が起きる）
      console.error("Error updating order statuses: ", error);
      throw error;
    } finally {
      dispatch(hideLoadingAction());
    }
  };
};

// 日付のみの更新(ステータスは変更しない)。
//   orders … 対象の注文doc配列(同一カート内の複数明細にまとめて適用できる)
//   input  … { placed_at?, ordered_at?, canceled_at? }
//            キーが無いフィールドは変更しない。nullでクリア。
//            検収日(received_at)は自動記録のため、渡しても無視される
//            (functions/order/orderDateUpdate.js の buildDateEditUpdate)。
// 変更が無い注文は書き込みをスキップする。
export const updateOrderDatesToFireStore = async (orders, input, userInfo) => {
  const now = new Date();
  const targets = toTargetList(orders);
  try {
    return await commitInChunks(targets, (order) => {
      const { fields, changes } = buildDateEditUpdate({ order, input, now });
      // 変更が無い注文には書き込まない(無駄な更新履歴を残さない)
      if (changes.length === 0) {
        return null;
      }
      return composeUpdateData({
        fields,
        changes,
        type: CHANGE_LOG_TYPE_DATE,
        userInfo,
        now,
      });
    });
  } catch (error) {
    console.error("Error updating order dates: ", error);
    throw error;
  }
};
